import type { CustomField, CustomFieldObject } from '@clockwise/clients/custom-fields';
import { Skeleton } from '@/components/ui/skeleton';
import { useCustomFieldDefinitions } from '@/lib/use-custom-field-definitions';

interface CustomFieldsViewerProps {
  object: CustomFieldObject;
  values: Record<string, unknown> | null | undefined;
  /** Optional override for the section heading. Defaults to "Custom fields". */
  heading?: string;
  /** Optional test seam — see CustomFieldsEditor for the contract. */
  definitions?: readonly CustomField[];
}

/**
 * Read-only display of an object's custom-field values. Only renders fields
 * that have a non-empty value; if nothing's set (or no definitions exist),
 * renders `null` so it's safe to drop into any detail page.
 */
export function CustomFieldsViewer({
  object,
  values,
  heading = 'Custom fields',
  definitions: definitionsProp,
}: CustomFieldsViewerProps): React.ReactElement | null {
  const query = useCustomFieldDefinitions(object);
  const definitions = definitionsProp ?? query.data;
  const isLoading = definitionsProp == null && query.isLoading;

  if (isLoading) {
    return (
      <div className="space-y-2">
        <Skeleton className="h-4 w-24" />
        <Skeleton className="h-4 w-48" />
      </div>
    );
  }

  if (!definitions || definitions.length === 0) return null;

  const safe = values ?? {};
  const populated = definitions.filter((def) => hasValue(safe[def.id]));
  if (populated.length === 0) return null;

  return (
    <section className="space-y-2">
      <h3 className="text-sm font-medium text-muted-foreground">{heading}</h3>
      <dl className="grid grid-cols-1 gap-2 sm:grid-cols-2">
        {populated.map((def) => (
          <div key={def.id} className="text-sm">
            <dt className="text-muted-foreground">{def.label}</dt>
            <dd className="font-medium">{formatValue(def.fieldType, safe[def.id])}</dd>
          </div>
        ))}
      </dl>
    </section>
  );
}

function hasValue(v: unknown): boolean {
  if (v == null) return false;
  if (typeof v === 'string') return v.length > 0;
  if (Array.isArray(v)) return v.length > 0;
  return true;
}

function formatValue(fieldType: string, value: unknown): React.ReactNode {
  if (value == null) return '—';
  switch (fieldType) {
    case 'checkbox':
      return value ? 'Yes' : 'No';
    case 'multi_select':
      if (Array.isArray(value)) {
        return (
          <span className="inline-flex flex-wrap gap-1">
            {value.map((v) => (
              <span key={v} className="rounded bg-muted px-1.5 py-0.5 text-xs">
                {v}
              </span>
            ))}
          </span>
        );
      }
      return String(value);
    case 'date':
      if (typeof value === 'string') {
        const d = new Date(value);
        return Number.isNaN(d.getTime()) ? value : d.toLocaleDateString();
      }
      return String(value);
    default:
      return String(value);
  }
}
